import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Lock, User } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { adminApi, setToken, getToken } from '@/lib/adminApi';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (!getToken()) return;
    adminApi
      .me()
      .then(() => navigate('/admin/dashboard', { replace: true }))
      .catch(() => {});
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }
    setSubmitting(true);
    try {
      const { access_token } = await adminApi.login(username, password);
      setToken(access_token);
      toast.success('Welcome back');
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 blur-[120px] rounded-full" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary/10 blur-[100px] rounded-full" />

      <Card
        data-testid="admin-login-card"
        className="relative z-10 w-full max-w-md p-8 bg-card/80 backdrop-blur-sm border-border/50"
      >
        <div className="text-center mb-8">
          <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
            Admin Portal
          </Badge>
          <h1 className="text-3xl font-bold mb-2">
            <span className="text-gradient">Syncline</span> Admin
          </h1>
          <p className="text-sm text-muted-foreground">Sign in to manage orders & quotes</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              Username
            </Label>
            <Input
              id="username"
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              autoComplete="username"
              className="bg-background border-border/50 focus:border-primary"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium flex items-center gap-2">
              <Lock className="h-4 w-4 text-primary" />
              Password
            </Label>
            <Input
              id="password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
              className="bg-background border-border/50 focus:border-primary"
            />
          </div>

          <Button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            size="lg"
            className="w-full bg-gradient-primary hover:shadow-glow transition-all duration-300 group"
          >
            {submitting ? 'Signing in…' : 'Sign In'}
            <LogIn className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </form>

        <p className="text-xs text-muted-foreground text-center mt-6">
          Authorized personnel only. All actions are logged.
        </p>
      </Card>
    </div>
  );
}
